/**
 * FE-C2g batch-2: Devices route — contract paths for devices.route.js.
 */

import { buildContractPath } from "./api/contract-client.js";

export const DEVICES_CONSOLE_TIMEOUT_MS = 16000;
export const DEVICES_FIRMWARE_HINTS_TIMEOUT_MS = 25000;

export function buildGetDevicesPath() {
  return buildContractPath("get__devices");
}

/** @param {{ skipCache?: boolean; bbox?: { min_lng: number; min_lat: number; max_lng: number; max_lat: number } }} [opts] */
export function buildGetDevicesMapSnapshotPath(opts = {}) {
  /** @type {Record<string, string>} */
  const query = {};
  if (opts.skipCache) query.skip_cache = "1";
  const bb = opts.bbox;
  if (bb && Number.isFinite(bb.min_lng) && Number.isFinite(bb.min_lat) && Number.isFinite(bb.max_lng) && Number.isFinite(bb.max_lat)) {
    query.min_lng = String(bb.min_lng);
    query.min_lat = String(bb.min_lat);
    query.max_lng = String(bb.max_lng);
    query.max_lat = String(bb.max_lat);
  }
  return buildContractPath(
    "get__devices_map-snapshot",
    Object.keys(query).length ? { query } : undefined,
  );
}

export function buildPostDevicesMapResolvePath() {
  return buildContractPath("post__devices_map-resolve");
}

export function buildPostDevicesBulkProfilePath() {
  return buildContractPath("post__devices_bulk_profile");
}

export function buildDevicesFirmwareHintsPath() {
  return buildContractPath("get__devices_firmware-hints");
}

/** @param {string} deviceId */
export function buildGetDevicePath(deviceId) {
  return buildContractPath("get__devices_p", { pathParams: [deviceId] });
}

/** @param {string} deviceId @param {number} [sinceHours] */
export function buildDeviceAlarmFeedPath(deviceId, sinceHours = 24) {
  return buildContractPath("get__devices_p_alarm-feed", {
    pathParams: [deviceId],
    query: { since_hours: sinceHours },
  });
}

/** @param {string} deviceId @param {number} [limit] */
export function buildDeviceMessagesPath(deviceId, limit = 25) {
  return buildContractPath("get__devices_p_messages", {
    pathParams: [deviceId],
    query: { limit },
  });
}

/** @param {string} deviceId */
export function buildPatchDeviceProfilePath(deviceId) {
  return buildContractPath("patch__devices_p_profile", { pathParams: [deviceId] });
}

/** @param {string} deviceId @param {number} durationMs */
export function buildPostDeviceAlertOnPath(deviceId, durationMs) {
  return buildContractPath("post__devices_p_alert_on", {
    pathParams: [deviceId],
    query: { duration_ms: durationMs },
  });
}

/** @param {string} deviceId */
export function buildPostDeviceAlertOffPath(deviceId) {
  return buildContractPath("post__devices_p_alert_off", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPostDeviceSelfTestPath(deviceId) {
  return buildContractPath("post__devices_p_self-test", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPostDeviceScheduleRebootPath(deviceId) {
  return buildContractPath("post__devices_p_schedule-reboot", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPostDeviceUnrevokePath(deviceId) {
  return buildContractPath("post__devices_p_unrevoke", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPostDeviceCommandsPath(deviceId) {
  return buildContractPath("post__devices_p_commands", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPostDeviceDeleteResetPath(deviceId) {
  return buildContractPath("post__devices_p_delete-reset", { pathParams: [deviceId] });
}

/** @param {string} deviceId @param {string} taskId */
export function buildGetDeviceProvisionWifiTaskPath(deviceId, taskId) {
  return buildContractPath("get__devices_p_provision_wifi-task_p", {
    pathParams: [deviceId, taskId],
  });
}

/** @param {string} deviceId */
export function buildPostDeviceProvisionWifiTaskPath(deviceId) {
  return buildContractPath("post__devices_p_provision_wifi-task", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildGetDeviceTriggerPolicyPath(deviceId) {
  return buildContractPath("get__devices_p_trigger-policy", { pathParams: [deviceId] });
}

/** @param {string} deviceId */
export function buildPutDeviceTriggerPolicyPath(deviceId) {
  return buildContractPath("put__devices_p_trigger-policy", { pathParams: [deviceId] });
}
